import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Grid,
  Flex,
  Typography,
  H1,
  H2,
  H3,
  P,
  Link,
  colors,
  spacing,
  useI18nContext,
} from '@procore/core-react';
import { ArrowRight } from '@procore/core-icons';
import * as Styles from '@/react/styles';

const PricingSection = ({
  pricingDescription,
  subscriptionRequired,
  connectorRequired,
  hasTrials,
  pricingUrl,
  email,
  soldBy,
}) => {
  const i18n = useI18nContext();

  return (
    <>
      {!pricingDescription && (
        <Box padding="xl">
          <Flex
            direction="row"
            padding="xl"
            gap="lg"
            data-testid="pricing-not-available-section"
          >
            <img
              src={window.imagePath('icons/Invoice_96x96.svg')}
              alt=""
              style={{ width: '96px', height: '96px' }}
            />
            <Box display="block">
              <H2 className="font-size-h2">
                {i18n.t('appDetails.pricing.titleNoPricing')}
              </H2>
              <P className="font-size-body color-concrete">
                {i18n.t('appDetails.pricing.descriptionNoPricing')}
                <Link href={`mailto:${email}`}>
                  <Typography
                    className="font-size-body"
                    style={{ color: `${colors.blue45}` }}
                  >
                    {i18n.t('appDetails.pricing.contactPartner')}
                  </Typography>
                </Link>
              </P>
            </Box>
          </Flex>
        </Box>
      )}

      {pricingDescription && (
        <Box
          display="flex-column"
          padding="xxl"
          margin="lg"
          alignContent="flex-start"
          justifyContent="flex-start"
          data-testid="pricing-description"
        >
          <Grid
            gutterX={{ tabletLg: 'xxl' }}
            gutterY={{ tabletSm: 'xl' }}
            data-testid="pricing-section"
          >
            <Grid.Row>
              <Grid.Col colWidth={{ tabletLg: 6, tabletSm: 12 }}>
                <H1 className="font-size-h1">
                  {i18n.t('appDetails.pricing.title')}
                </H1>
                <Box marginTop="sm">
                  <H3 className="font-size-md" data-testid="pricing-soldby">
                    {i18n.t('appDetails.pricing.soldBy')}
                    {soldBy}
                  </H3>
                  <P className="font-size-body color-concrete">
                    {pricingDescription}
                  </P>
                  {pricingUrl && (
                    <H2 className="font-size-body" data-testid="pricing-url">
                      <Styles.OrangeLink
                        href={pricingUrl}
                        className="font-size-body"
                        target="_blank"
                      >
                        {i18n.t('appDetails.pricing.website')}
                        <ArrowRight
                          style={{
                            paddingTop: `${spacing.sm}px`,
                            fontWeight: 'bold',
                          }}
                        />
                      </Styles.OrangeLink>
                    </H2>
                  )}
                </Box>
              </Grid.Col>

              <Grid.Col
                colWidth={{ tabletLg: 6, tabletSm: 12 }}
                data-testid="pricing-faqs"
              >
                <H2>{i18n.t('appDetails.pricing.faqs')}</H2>

                <Box
                  padding="md"
                  marginTop="sm"
                  marginBottom="xl"
                  data-testid="pricing-faqs-header"
                  style={{ backgroundColor: `${colors.gray96}` }}
                >
                  <Typography className="font-size-sm color-concrete">
                    {i18n.t('appDetails.pricing.faqsHeaderPart1')}
                  </Typography>
                  <Link href="#support-section">
                    <Typography
                      className="font-size-sm"
                      style={{
                        color: `${colors.blue45}`,
                        textDecoration: 'underline',
                      }}
                    >
                      {i18n.t('appDetails.pricing.faqsHeaderPart2')}
                    </Typography>
                  </Link>
                  <Typography className="font-size-sm color-concrete">
                    {i18n.t('appDetails.pricing.faqsHeaderPart3')}
                  </Typography>
                </Box>

                <Box marginBottom="xl" data-testid="pricing-subscription">
                  <H3 className="font-size-md">
                    {i18n.t(
                      'appDetails.pricing.subscriptionRequired.description'
                    )}
                  </H3>
                  <P className="font-size-body color-concrete">
                    {i18n.t(
                      `appDetails.pricing.subscriptionRequired.${
                        subscriptionRequired ? 'yes' : 'no'
                      }`
                    )}
                  </P>
                </Box>

                <Styles.Divider color={colors.gray70} />

                <Box
                  marginBottom="xl"
                  marginTop="xl"
                  data-testid="pricing-connector"
                >
                  <H3 className="font-size-md">
                    {i18n.t(
                      'appDetails.pricing.thirdPartyConnectorRequired.description'
                    )}
                  </H3>
                  <P className="font-size-body color-concrete">
                    {i18n.t(
                      `appDetails.pricing.thirdPartyConnectorRequired.${
                        connectorRequired ? 'yes' : 'no'
                      }`
                    )}
                  </P>
                </Box>

                <Styles.Divider color={colors.gray70} />

                <Box marginTop="xl" data-testid="pricing-freetrial">
                  <H3 className="font-size-md">
                    {i18n.t('appDetails.pricing.freeTrialOffered.description')}
                  </H3>
                  <P className="font-size-body color-concrete">
                    {i18n.t(
                      `appDetails.pricing.freeTrialOffered.${
                        hasTrials ? 'yes' : 'no'
                      }`
                    )}
                  </P>
                </Box>
              </Grid.Col>
            </Grid.Row>
          </Grid>
        </Box>
      )}
    </>
  );
};

PricingSection.propTypes = {
  pricingDescription: PropTypes.string,
  subscriptionRequired: PropTypes.bool,
  connectorRequired: PropTypes.bool,
  hasTrials: PropTypes.bool,
  pricingUrl: PropTypes.string,
  email: PropTypes.string,
  soldBy: PropTypes.string,
};

export default PricingSection;
